import type { ReactNode } from 'react';
import { Icon } from '../atoms/Icon';
import { useLinkComponent } from '../navigation';
import type { MenuItem } from './Menu';
import { orderRowActions, RowActions, useRowActions } from './RowActions';
import styles from './ListRow.module.css';

export interface ListRowProps {
  /** Avatar or small symbol on the left. */
  leading?: ReactNode;
  title: ReactNode;
  /** One grey line below the title, facts separated by " · ". */
  description?: ReactNode;
  /** Value, status or a compact action on the right. */
  trailing?: ReactNode;
  /** Makes the whole row a link to a detail page (shows a chevron). */
  href?: string;
  /** Makes the whole row a button, e.g. "Abmelden" (no chevron). */
  onClick?: () => void;
  /** `danger`: red title for a destructive action like signing out. */
  tone?: 'default' | 'danger';
  as?: 'li' | 'div';
  /**
   * The `…` menu at the end of the row (#197), also on right-click, long
   * press and the context-menu key; it takes the chevron's place.
   */
  actions?: (MenuItem | false | null | undefined)[];
  /** Accessible name of the `…` button, e.g. "Weitere Aktionen: Anni". */
  actionsLabel?: string;
}

/**
 * One row in a grouped list, like an iOS table cell (docs/ux-richtlinien.md
 * §6.1): title, grey subtitle, value on the right, chevron if it navigates.
 */
export function ListRow({
  leading,
  title,
  description,
  trailing,
  href,
  onClick,
  tone = 'default',
  as: Element = 'li',
  actions,
  actionsLabel = '',
}: ListRowProps) {
  const Link = useLinkComponent();
  const items = orderRowActions(actions ?? []);
  const menu = useRowActions(items.length > 0);
  return (
    <Element
      className={[styles.row, (href || onClick) && styles.link, tone === 'danger' && styles.danger]
        .filter(Boolean)
        .join(' ')}
      {...(items.length > 0 ? menu.rowProps : {})}
    >
      {leading && <div className={styles.leading}>{leading}</div>}
      <div className={styles.text}>
        <div className={styles.title}>
          {href ? (
            <Link href={href} className={styles.anchor}>
              {title}
            </Link>
          ) : onClick ? (
            <button type="button" className={`${styles.anchor} ${styles.button}`} onClick={onClick}>
              {title}
            </button>
          ) : (
            title
          )}
        </div>
        {description && <div className={styles.description}>{description}</div>}
      </div>
      {trailing && <div className={styles.trailing}>{trailing}</div>}
      {items.length > 0 ? (
        <RowActions label={actionsLabel} items={items} state={menu} />
      ) : (
        href && <Icon name="chevron-right" size="sm" className={styles.chevron} />
      )}
    </Element>
  );
}

/** Container for `ListRow`s with inset separators. */
export function List({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <ul className={styles.list} {...(label ? { 'aria-label': label } : {})}>
      {children}
    </ul>
  );
}
